import React, { useEffect, useRef, useState } from 'react';
import { api, User } from './api';
import { useAuth } from './ChatAuth';
import { getPref, usePrefSetter } from './prefs';
import { Icon } from './icons';

const STATUS_PRESETS = [
  { emoji: '📅', text: 'In a meeting' },
  { emoji: '🎧', text: 'Focusing' },
  { emoji: '🏝️', text: 'Vacationing' },
  { emoji: '🤒', text: 'Out sick' },
  { emoji: '🏠', text: 'Working remotely' },
  { emoji: '🍽️', text: 'At lunch' },
];

const PAUSE_OPTIONS: { label: string; minutes: number | null }[] = [
  { label: 'For 30 minutes', minutes: 30 },
  { label: 'For 1 hour', minutes: 60 },
  { label: 'For 2 hours', minutes: 120 },
  { label: 'Until tomorrow', minutes: -1 },
  { label: 'Until I resume', minutes: null },
];

export function AccountMenu({
  user,
  style,
  onOpenProfile,
  onOpenPreferences,
  onOpenDownloads,
  onClose,
}: {
  user: User;
  style?: React.CSSProperties;
  onOpenProfile: () => void;
  onOpenPreferences: (cat?: string) => void;
  onOpenDownloads: () => void;
  onClose: () => void;
}) {
  const { patchUser, logout } = useAuth();
  const set = usePrefSetter(user, patchUser);
  const ref = useRef<HTMLDivElement>(null);
  const [editingStatus, setEditingStatus] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [emoji, setEmoji] = useState(user.status_emoji || '');
  const [text, setText] = useState(user.status_text || '');

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const away = user.presence === 'away';
  const name = user.name || user.email.split('@')[0];
  const initial = (name[0] || '?').toUpperCase();

  const pausedUntil = getPref<string | null>(user.prefs, 'notifications.paused_until', null);
  const pausedLabel = !user.notifications_paused
    ? 'On'
    : pausedUntil
      ? `Paused · ${new Date(pausedUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
      : 'Paused';

  const saveStatus = async () => {
    patchUser(await api.updateProfile({ status_emoji: emoji || undefined, status_text: text || undefined }));
    setEditingStatus(false);
  };
  const clearStatus = async () => {
    patchUser(await api.updateProfile({ clear_status: true }));
    setEmoji('');
    setText('');
    setEditingStatus(false);
  };
  const togglePresence = async () => patchUser(await api.updatePresence(away ? 'active' : 'away'));

  const pauseFor = async (minutes: number | null) => {
    let until: string | null = null;
    if (minutes === -1) {
      const t = new Date();
      t.setDate(t.getDate() + 1);
      t.setHours(8, 0, 0, 0);
      until = t.toISOString();
    } else if (minutes) {
      until = new Date(Date.now() + minutes * 60000).toISOString();
    }
    patchUser(await api.updateNotifications(true, until));
    setPauseOpen(false);
  };
  const resume = async () => {
    patchUser(await api.updateNotifications(false, null));
    setPauseOpen(false);
  };

  return (
    <div className="fx-acctmenu" ref={ref} style={style}>

      <div className="fx-acct-head">
        <div className="fx-acct-av" style={avatarStyle(user)}>
          {user.avatar_url ? '' : initial}
        </div>
        <div className="fx-acct-id">
          <div className="fx-acct-name">{name}</div>
          <div className="fx-acct-presence">
            <span className={`fx-presence-dot${away ? ' is-away' : ''}`} />
            {away ? 'Away' : 'Active'}
          </div>
        </div>
      </div>

      {user.status_text || user.status_emoji ? (
        <button type="button" className="fx-acct-status-cur" onClick={() => setEditingStatus((v) => !v)}>
          <span>{user.status_emoji}</span>
          <span className="fx-acct-status-txt">{user.status_text}</span>
          <span
            className="fx-acct-status-clear"
            onClick={(e) => { e.stopPropagation(); clearStatus(); }}
          >
            ×
          </span>
        </button>
      ) : null}

      {editingStatus ? (
        <div className="fx-status-edit">
          <div className="fx-status-inputs">
            <input
              className="fx-text-input fx-status-emoji"
              value={emoji}
              onChange={(e) => setEmoji(e.target.value)}
              placeholder="😀"
              maxLength={4}
            />
            <input
              className="fx-text-input fx-status-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What's your status?"
              autoFocus
            />
          </div>
          <div className="fx-status-presets">
            {STATUS_PRESETS.map((s) => (
              <button
                type="button"
                key={s.text}
                className="fx-status-preset"
                onClick={() => { setEmoji(s.emoji); setText(s.text); }}
              >
                {s.emoji} {s.text}
              </button>
            ))}
          </div>
          <div className="fx-status-actions">
            <button type="button" className="fx-btn" onClick={() => setEditingStatus(false)}>Cancel</button>
            <button type="button" className="fx-sendbtn" onClick={saveStatus}>Save</button>
          </div>
        </div>
      ) : (
        <button type="button" className="fx-acct-item fx-acct-updatestatus" onClick={() => setEditingStatus(true)}>
          <Icon name="smiley" size={16} /> Update your status
        </button>
      )}

      <div className="fx-acct-sep" />

      <button type="button" className="fx-acct-item" onClick={togglePresence}>
        Set yourself as <b>&nbsp;{away ? 'active' : 'away'}</b>
      </button>
      <div className="fx-acct-subwrap">
        <button type="button" className="fx-acct-item" onClick={() => setPauseOpen((v) => !v)}>
          Notifications
          <span className="fx-acct-right">
            {pausedLabel} <Icon name="chevronRight" size={13} />
          </span>
        </button>
        {pauseOpen && (
          <div className="fx-acct-submenu">
            <div className="fx-acct-sub-title">Pause notifications…</div>
            {PAUSE_OPTIONS.map((o) => (
              <button type="button" key={o.label} className="fx-acct-item" onClick={() => pauseFor(o.minutes)}>
                {o.label}
              </button>
            ))}
            {user.notifications_paused && (
              <>
                <div className="fx-acct-sep" />
                <button type="button" className="fx-acct-item" onClick={resume}><b>Resume notifications</b></button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="fx-acct-sep" />

      <button type="button" className="fx-acct-item" onClick={() => { onOpenProfile(); onClose(); }}>Profile</button>
      <button type="button" className="fx-acct-item" onClick={() => { onOpenPreferences(); onClose(); }}>Preferences</button>
      <button type="button" className="fx-acct-item" onClick={() => { onOpenDownloads(); onClose(); }}>
        Downloads
        <span className="fx-acct-right">Ctrl+Shift+J</span>
      </button>

      <div className="fx-acct-sep" />

      <button type="button" className="fx-acct-item fx-acct-signout" onClick={logout}>
        Sign out of FxM
      </button>
    </div>
  );
}

/** Stable hue (0-359) from any identity string — powers per-person avatar colour. */
export function hueOf(s: string | null | undefined): number {
  const str = s || '?';
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h % 360;
}

export function avatarStyle(u: {
  avatar_url?: string | null;
  id?: string;
  name?: string | null;
  email?: string | null;
}): React.CSSProperties {
  const style: Record<string, string | number> = { '--h': hueOf(u.id || u.name || u.email) };
  if (u.avatar_url) style.backgroundImage = `url(${u.avatar_url})`;
  return style as React.CSSProperties;
}
